import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, Loader2, FormInput } from "lucide-react";

export function TestPage() {
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formModalData, setFormModalData] = useState("");

  // Form state
  const [input1, setInput1] = useState("");
  const [input2, setInput2] = useState("");
  const [selectedOption, setSelectedOption] = useState("");
  const [isChecked, setIsChecked] = useState(false);

  const handleTriggerError = () => {
    setIsErrorModalOpen(true);
  };

  const handleTriggerLoading = () => {
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 3000);
  };

  const handleOpenFormModal = () => {
    setFormModalData("henrik");
    setIsFormModalOpen(true);
  };

  const handleFormSubmit = () => {
    console.log("Form submitted:", {
      passedData: formModalData,
      input1,
      input2,
      selectedOption,
      isChecked,
    });
    setIsFormModalOpen(false);
    // Reset form
    setInput1("");
    setInput2("");
    setSelectedOption("");
    setIsChecked(false);
  };

  return (
    <div className="min-h-screen bg-background p-8">
      <h1 className="text-2xl font-bold mb-8">Test Page</h1>

      <div className="space-y-6">
        {/* Buttons Box */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h2 className="text-lg font-semibold mb-4">Actions</h2>
          <div className="flex flex-wrap gap-4">
            <Button variant="destructive" onClick={handleTriggerError}>
              <AlertTriangle className="w-4 h-4 mr-2" />
              Trigger Error Modal
            </Button>
            <Button onClick={handleTriggerLoading} disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                "Trigger Loading Spinner"
              )}
            </Button>
            <Button variant="secondary" onClick={handleOpenFormModal}>
              <FormInput className="w-4 h-4 mr-2" />
              Open Form Modal
            </Button>
          </div>
        </div>

        {/* Spinner Box */}
        <div className="bg-card border border-border rounded-xl p-6 min-h-[200px] flex items-center justify-center relative">
          <h2 className="text-lg font-semibold absolute top-6 left-6">Spinner Area</h2>
          {isLoading ? (
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="w-12 h-12 animate-spin text-primary" />
              <p className="text-muted-foreground">Loading in progress...</p>
            </div>
          ) : (
            <p className="text-muted-foreground">Click the button above to see the spinner</p>
          )}
        </div>
      </div>

      {/* Error Modal */}
      <Dialog open={isErrorModalOpen} onOpenChange={setIsErrorModalOpen}>
        <DialogContent className="bg-card/60 backdrop-blur-xl border-border/50">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Error Occurred
            </DialogTitle>
            <DialogDescription className="pt-4">
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 text-destructive">
                <p className="font-mono text-sm">
                  Error: Something went wrong while processing your request.
                </p>
                <p className="font-mono text-sm mt-2">
                  Stack trace: at TestPage.handleTriggerError (TestPage.tsx:18)
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={() => setIsErrorModalOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Form Modal */}
      <Dialog open={isFormModalOpen} onOpenChange={setIsFormModalOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Form Modal</DialogTitle>
            <DialogDescription>
              Passed data: <span className="font-semibold text-primary">{formModalData}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="input1">First Input</Label>
              <Input
                id="input1"
                placeholder="Enter first value"
                value={input1}
                onChange={(e) => setInput1(e.target.value)}
                className="bg-white"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="input2">Second Input</Label>
              <Input
                id="input2"
                placeholder="Enter second value"
                value={input2}
                onChange={(e) => setInput2(e.target.value)}
                className="bg-white"
              />
            </div>

            <div className="space-y-2">
              <Label>Select Option</Label>
              <Select value={selectedOption} onValueChange={setSelectedOption}>
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder="Choose an option" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border z-50">
                  <SelectItem value="option1">Option 1</SelectItem>
                  <SelectItem value="option2">Option 2</SelectItem>
                  <SelectItem value="option3">Option 3</SelectItem>
                  <SelectItem value="option4">Option 4</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="terms"
                checked={isChecked}
                onCheckedChange={(checked) => setIsChecked(checked === true)}
              />
              <Label htmlFor="terms" className="cursor-pointer">
                I agree to the terms and conditions
              </Label>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setIsFormModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleFormSubmit}>
              Submit
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
